<template>
    <div class="stripe-profile-container">
        <div id="preloader" v-if="load" class="preloader"></div>
        <p class="title">Get Paid</p>
        <div class="divider"></div>
        <div
            v-if="stripeProfileInfo.stripe_connected && stripeProfileInfo.has_bank_acct"
            class="stripe-profile"
        >
            <div class="get-paid-container">
                <div class="get-paid-data-container">
                    <div class="payouts-enabled">
                        <p class="label">Payment</p>
                        <p class="data">{{ paymentsEnabled }}</p>
                    </div>
                    <div class="payouts-interval">
                        <p class="label">Payouts</p>
                        <p class="data">
                            {{ capitalize(stripeProfileInfo.payouts_interval) }}
                        </p>
                    </div>
                    <div class="external-acct">
                        <p class="label">External Account</p>
                        <div class="data">
                            <p class="name">
                                {{ stripeProfileInfo.external_account_name }}
                            </p>
                            <p class="last4">
                                {{ stripeProfileInfo.external_account_last4 }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="account-balances-container">
                <p class="title">Account Balances</p>
                <div class="account-balances-data-container">
                    <div class="total-balance">
                        <p class="label">Total balance</p>
                        <p class="data">
                            {{ formatCurrency(stripeProfileInfo.total_stripe_balance) }}
                        </p>
                    </div>
                    <div class="future-payouts">
                        <p class="label">Future Payouts</p>
                        <p class="data">
                            {{ formatCurrency(stripeProfileInfo.future_stripe_payouts) }}
                        </p>
                    </div>
                    <div class="in-transit-payouts">
                        <p class="label">In Transit to Bank</p>
                        <p class="data">
                            {{ formatCurrency(stripeProfileInfo.in_transit) }}
                        </p>
                    </div>
                    <div class="lifetime-total-payouts">
                        <p class="label">Lifetime Total Volume</p>
                        <p class="data">
                            {{ formatCurrency(stripeProfileInfo.total_payout) }}
                        </p>
                    </div>
                </div>
            </div>
            <div v-if="stripeProfileInfo.money_movements.length" class="move-money-container">
                <p class="title">Money Movement</p>
                <div class="money-movement-data-container">
                    <div class="movement-labels">
                        <p
                            v-for="(header, indx) in movementHeaders"
                            :key="indx"
                            class="movement-header"
                        >
                            {{ header }}
                        </p>
                    </div>
                    <div class="movement-data-wrapper">
                        <div
                            v-for="(payout, indx) in stripeProfileInfo.money_movements"
                            :key="indx"
                            class="movement-data"
                        >
                            <p class="amount">
                                {{ formatCurrency(payout['total-amount']) }}
                            </p>
                            <div class="external-acct">
                                <p class="name">
                                    {{ stripeProfileInfo.external_account_name }}
                                </p>
                                <p class="last4">
                                    {{ stripeProfileInfo.external_account_last4 }}
                                </p>
                            </div>
                            <p class="initiated-date">
                                {{ formatDate(payout['created-timestamp']) }}
                            </p>
                            <p class="arrival-date">
                                {{ formatDate(payout['arrival-timestamp']) }}
                            </p>
                            <p class="badge badge-pill badge-primary transaction-status">
                                {{ capitalize(payout.status) }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <button @click="redirectToStripe" class="action-btn update-stripe-btn">
                Update Your Stripe Account
            </button>
        </div>
        <div
            v-else-if="stripeProfileInfo.stripe_connected && !stripeProfileInfo.has_bank_acct"
            class="stripe-profile"
        >
            <AddBankForm @add-bank-info="handleAddBank" />
        </div>
        <div v-else class="stripe-profile">
            <button @click="redirectToStripe" class="action-btn connect-stripe-btn">
                Connect Stripe
            </button>
        </div>
        <div v-if="componentMsg" class="component-msg">
            {{ componentMsg }}
        </div>
    </div>
</template>
<script>
    import axios from 'axios'
    import AddBankForm from './AddBankForm.vue'
    import useNotification from '@/composables/useNotification.js'

    const URLS = {
        connectStripe: '/profile/create/account',
        stripeProfileInitAPI: '/profile/payment-details/get-user-stripe-info',
        addBank: '/profile/add/bank/account'
    }

    export default {
        name: 'StripeProfile',

        components: { AddBankForm },

        setup() {
            const { notification } = useNotification()

            return { notification }
        },
        data: () => ({
            showUnloadMsg: true,
            load: false,
            stripeProfileInfo: {
                stripe_connected: false,
                has_bank_acct: false,
                payouts_enabled: false,
                payouts_interval: '',
                external_account_name: '',
                external_account_last4: '',
                total_stripe_balance: 0,
                future_stripe_payouts: 0,
                in_transit: 0,
                total_payout: 0,
                money_movements: []
            },
            movementHeaders: [
                'Amount',
                'External Account',
                'Initiated',
                'Est.Arrival',
                'Status'
            ],
            componentMsg: ''
        }),

        methods: {
            loadInitialData(data) {
                for (const [key, val] of Object.entries(data)) {
                    this.stripeProfileInfo[key] = val
                }
            },

            async initStripeProfileData() {
                try {
                    this.load = true
                    const resp = await axios.get(URLS.stripeProfileInitAPI)

                    this.loadInitialData(resp.data)
                } catch {
                    this.stripeProfileInfo.external_account_name = ''
                    this.notification('error', 'Could not fetch initial stripe profile data.')
                } finally {
                    this.load = false
                }
            },

            formatCurrency(val) {
                let currencyFormat = new Intl.NumberFormat('en-US')

                if (val) {
                    let amount = currencyFormat.format(val)

                    if (!amount.includes('.')) {
                        amount = amount + '.00'
                    }
                    return '$' + amount + ' USD'
                }

                return '$0.00 USD'
            },

            formatDate(utimestamp) {
                let date = new Date(utimestamp * 1000).toDateString().split(' ')
                return `${date[1]} ${date[2]}`
            },

            capitalize(str) {
                if (!str) return 'N/A'
                return str.charAt(0).toUpperCase() + str.slice(1)
            },

            redirectToStripe() {
                window.location.href = URLS.connectStripe
            },

            async handleAddBank(data) {
                try {
                    this.load = true
                    await axios.post(URLS.addBank, data)
                    this.showUnloadMsg = false
                    window.location.reload()
                } catch (err) {
                    this.notification('error', err)
                } finally {
                    this.load = false
                }
            },

            beforeWindowUnload(e) {
                if (
                    this.showUnloadMsg &&
                    this.stripeProfileInfo.stripe_connected &&
                    !this.stripeProfileInfo.has_bank_acct
                ) {
                    e.preventDefault()
                    e.returnValue = ''
                }
            }
        },

        computed: {
            paymentsEnabled: (self) => {
                if (self.stripeProfileInfo.payouts_enabled) {
                    return 'Enabled'
                } else {
                    return 'Disabled'
                }
            }
        },

        async created() {
            await this.initStripeProfileData()
        },
        mounted() {
            window.addEventListener('beforeunload', this.beforeWindowUnload)
        },
        beforeDestroy() {
            window.removeEventListener('beforeunload', this.beforeWindowUnload)
        }
    }
</script>
<style lang="scss" scoped>
    $mobileMax: 600px;
    @media screen and (max-width: $mobileMax) {
        .stripe-profile-container {
            padding-left: 10px !important;

            .action-btn {
                width: 100% !important;
            }

            .get-paid-container {
                .get-paid-data-container {
                    gap: 20px !important;

                    .external-acct {
                        margin-left: 0px !important;
                    }
                }
            }
            .account-balances-container {
                min-width: auto !important;

                .account-balances-data-container {
                    gap: 20px !important;

                    .in-transit-payouts {
                        margin-left: 0px !important;
                    }
                }
            }
        }
    }

    .stripe-profile-container {
        min-height: 1000px;
        padding-left: 80px;
        padding-top: 32px;
        max-width: 800px;
        width: 100%;
        background: #161618;

        p {
            margin-bottom: 0px;
            margin-left: 0px;
        }
        button {
            border: none;
        }

        .title {
            color: #dedede;
            font-family: Instrument Sans;
            font-size: 44px;
            font-style: normal;
            font-weight: 500;
            line-height: 110%;
            letter-spacing: -1.76px;
        }

        .divider {
            width: 100%;
            height: 1px;
            background: #646464;
            margin-top: 24px;
            margin-bottom: 24px;
        }
        .stripe-profile {
            font-family: Instrument Sans;
            margin-bottom: 50px;

            .label {
                font-weight: bold;
                margin-bottom: 0px;
                color: white;
                font-family: Inter;
                font-size: 16px;
                font-style: normal;
                font-weight: 700;
                line-height: 24px;
            }

            .data {
                color: white;
                font-family: Inter;
                font-size: 16px;
                font-style: normal;
                font-weight: 400;
                line-height: 24px;
            }

            .title {
                font-size: 24px;
                font-weight: bolder;
                margin-bottom: 20px;
            }

            .action-btn {
                padding: 16px 48px;
                background-color: #cdeb09;
                color: #161616;
                border-radius: 40px;
                font-family: Inter;
                font-size: 16px;
                font-style: normal;
                font-weight: 600;
                line-height: 24px;
            }

            .update-stripe-btn {
                margin-top: 20px;
            }

            .get-paid-container {
                margin-top: 20px;
                max-width: 600px;

                .get-paid-data-container {
                    display: flex;
                    flex-direction: row;
                    justify-content: left;
                    gap: 110px;
                    flex-wrap: wrap;

                    .external-acct {
                        margin-left: 36px;
                    }
                }
            }

            .account-balances-container {
                margin-top: 20px;
                min-width: 800px;

                .account-balances-data-container {
                    display: flex;
                    flex-direction: row;
                    justify-content: left;
                    gap: 70px;
                    flex-wrap: wrap;

                    .in-transit-payouts {
                        margin-left: 28px;
                    }
                }
            }

            .move-money-container {
                margin-top: 20px;

                .money-movement-data-container {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;

                    .movement-labels {
                        display: flex;
                        flex-direction: row;
                        justify-content: flex-start;
                        gap: 75px;

                        p:nth-child(2) {
                            margin-left: 36px;
                            min-width: 150px;
                        }

                        .movement-header {
                            color: white;
                            font-family: Inter;
                            font-size: 16px;
                            font-style: normal;
                            font-weight: 700;
                            line-height: 24px;
                        }
                    }

                    .movement-data-wrapper {
                        margin-top: 20px;
                        display: flex;
                        flex-direction: column;
                        gap: 15px;
                        min-width: 746px;

                        .movement-data {
                            display: flex;
                            flex-direction: row;
                            justify-content: flex-start;
                            align-items: baseline;
                            gap: 41px;

                            .amount {
                                max-width: 100px;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                white-space: nowrap;
                            }

                            p {
                                color: white;
                                font-family: Inter;
                                font-size: 16px;
                                font-style: normal;
                                font-weight: 400;
                                line-height: 24px;
                            }

                            .external-acct {
                                display: flex;
                                flex-direction: column;
                                gap: 3px;
                                justify-content: center;
                                align-items: flex-start;
                                margin-left: 32px;
                                max-width: 148px;
                            }

                            .initiated-date {
                                margin-left: 35px;
                            }

                            .arrival-date {
                                margin-left: 50px;
                            }

                            .transaction-status {
                                font-size: 10px;
                                color: white;
                                font-weight: bold;
                                background-color: #161618;
                                border: 1px solid white;
                                text-align: center;
                                border-radius: 16px;
                                padding: 4px 12px;
                                margin-left: 65px;
                            }
                        }
                    }
                }
            }
        }

        .component-msg {
            color: #dc3545;
        }
    }
</style>
